import React from "react";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import Typography from "@/ui/atoms/Typography";

import Card from "@/ui/molecules/Card";
import type { TUserConsent } from "@/apis/odin/apiODIN";

import { createViewFormattedDate } from "../ViewFormattedDate/createViewFormattedDate";
import type { TDateRegion } from "../ViewFormattedDate/types";

export type ViewUserPoliciesParams = {
    useState: () => {
        userConsent: TUserConsent;
        userRegion: TDateRegion | null;
    };
};

export type ViewUserPoliciesProps = { sx?: BoxProps["sx"] };

export const testIds = {
    main: "ViewUserPolicies",
};

export const createViewUserPolicies = ({
    useState,
}: ViewUserPoliciesParams) => {
    const ViewUserPolicies: React.FC<ViewUserPoliciesProps> = props => {
        const { sx } = props;
        const { userConsent } = useState();

        const ViewFormattedDate = createViewFormattedDate({
            useState,
        });

        return (
            <Box
                data-testid={testIds.main}
                sx={{
                    width: "100%",
                    height: "fit-content",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    padding: "3rem",
                    ...sx,
                }}
            >
                <Card
                    sx={{
                        width: "600px",
                        maxWidth: "100%",
                        gap: ".9em",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        padding: "2rem",
                    }}
                >
                    <Typography variant="h1">Terms and Conditions</Typography>
                    <Typography variant="body2">
                        <b>09/09/2024</b>
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontStyle: "italic",
                        }}
                    >
                        Accepted on:{" "}
                        {userConsent.userConsentValid &&
                        userConsent.userConsentLastAccepted !== null &&
                        userConsent.userConsentLastAccepted !== undefined ? (
                            <ViewFormattedDate
                                date={userConsent.userConsentLastAccepted}
                            />
                        ) : (
                            "Not accepted"
                        )}
                    </Typography>

                    <Typography variant="h1">
                        PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE
                        USING THIS SITE
                    </Typography>
                    <Typography variant="h2">
                        What&apos;s in these terms?
                    </Typography>
                    <Typography variant="body2">
                        These terms set out the rules for using any and all of
                        our websites, any subdomains, and such other websites or
                        subdomains which we may add from time to time (together
                        &quot;<b>our site</b>&quot;).
                    </Typography>

                    <Typography variant="h2">
                        Who we are and how to contact us
                    </Typography>
                    <Typography variant="body2">
                        We are PRODA LIMITED (&quot;<b>we</b>&quot;), and we
                        operate our site.
                    </Typography>
                    <Typography variant="body2">
                        Company Number: 10548504
                    </Typography>
                    <Typography variant="body2">
                        Email address for general enquiries:{" "}
                        <Typography
                            variant="body2"
                            sx={{
                                color: "var(--proda-neon-blue)",
                            }}
                            onClick={() => {
                                window.open("mailto:info@proda.ai", "_self");
                            }}
                        >
                            info@proda.ai
                        </Typography>
                    </Typography>
                    <Typography variant="body2">
                        Email address for data privacy enquiries:{" "}
                        <Typography
                            variant="body2"
                            sx={{
                                color: "var(--proda-neon-blue)",
                            }}
                            onClick={() => {
                                window.open("mailto:dpo@proda.ai", "_self");
                            }}
                        >
                            dpo@proda.ai
                        </Typography>
                    </Typography>
                    <Typography variant="body2">
                        Postal address: Blue Fin Building, 1st Floor, 110
                        Southwark Street, London, SE1 0SU
                    </Typography>
                    <Typography variant="body2">
                        Telephone number: +44 (0) 20 3740 3766
                    </Typography>

                    <Typography variant="h2">
                        By using our site you accept these terms
                    </Typography>
                    <Typography variant="body2">
                        By using our site, you confirm that you accept these
                        terms of use and that you agree to comply with them.
                    </Typography>
                    <Typography variant="body2">
                        If you do not agree to these terms, you must not use our
                        site.
                    </Typography>
                    <Typography variant="body2">
                        We recommend that you print a copy of these terms for
                        future reference.
                    </Typography>

                    <Typography variant="h2">
                        There are other terms that may apply to you
                    </Typography>
                    <Typography variant="body2">
                        These terms of use refer to the following additional
                        terms, which also apply to your use of our site:
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                    >
                        <Box component="li">
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "var(--proda-neon-blue)",
                                }}
                                onClick={() => {
                                    window.open(
                                        "https://proda.ai/privacy-policy/",
                                        "_self",
                                    );
                                }}
                            >
                                Our Privacy Policy
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "var(--proda-neon-blue)",
                                }}
                                onClick={() => {
                                    window.open(
                                        "https://proda.ai/cookie-policy/",
                                        "_self",
                                    );
                                }}
                            >
                                Our Cookie Policy
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="h2">
                        We may make changes to these terms
                    </Typography>
                    <Typography variant="body2">
                        We amend these terms from time to time. Every time you
                        wish to use our site, please check these terms to ensure
                        you understand the terms that apply at that time. These
                        terms were most recently updated on 9 September 2024.
                    </Typography>

                    <Typography variant="h2">
                        We may make changes to our site
                    </Typography>
                    <Typography variant="body2">
                        We may update and change our site from time to time
                        without notice to you to reflect changes to our
                        products, our users&apos; needs and our business
                        priorities.
                    </Typography>

                    <Typography variant="h2">
                        We may suspend or withdraw our site
                    </Typography>
                    <Typography variant="body2">
                        Our site is made available free of charge.
                    </Typography>
                    <Typography variant="body2">
                        We do not guarantee that our site, or any content on it,
                        will always be available or be uninterrupted. We may
                        suspend or withdraw or restrict the availability of all
                        or any part of our site for business and operational
                        reasons. We will try to give you reasonable notice of
                        any suspension or withdrawal.
                    </Typography>
                    <Typography variant="body2">
                        You are also responsible for ensuring that all persons
                        who access our site through your internet connection are
                        aware of these terms of use and other applicable terms
                        and conditions, and that they comply with them.
                    </Typography>

                    <Typography variant="h2">
                        We may transfer this agreement to someone else
                    </Typography>
                    <Typography variant="body2">
                        We may transfer our rights and obligations under these
                        terms to another organisation. We will always tell you
                        in writing if this happens and we will ensure that the
                        transfer will not affect your rights under the contract.
                    </Typography>

                    <Typography variant="h2">
                        Our site is directed to people residing in United
                        Kingdom. We do not represent that content available on
                        or through our site is appropriate for use or available
                        in other locations.
                    </Typography>

                    <Typography variant="h2">
                        You must keep your account details safe
                    </Typography>
                    <Typography variant="body2">
                        If you choose, or you are provided with, a user
                        identification code, password or any other piece of
                        information as part of our security procedures, you must
                        treat such information as confidential. You must not
                        disclose it to any third party.
                    </Typography>
                    <Typography variant="body2">
                        We have the right to disable any user identification
                        code or password, whether chosen by you or allocated by
                        us, at any time, if in our reasonable opinion you have
                        failed to comply with any of the provisions of these
                        terms of use.
                    </Typography>
                    <Typography variant="body2">
                        If you know or suspect that anyone other than you knows
                        your user identification code or password, you must
                        promptly notify us at the email contact details set out
                        at the beginning of the document.
                    </Typography>

                    <Typography variant="h2">
                        How you may use material on our site
                    </Typography>
                    <Typography variant="body2">
                        We are the owner or the licensee of all intellectual
                        property rights in our site, and in the material
                        published on it. Those works are protected by copyright
                        laws and treaties around the world. All such rights are
                        reserved.
                    </Typography>
                    <Typography variant="body2">
                        You may print off one copy, and may download extracts,
                        of any page(s) from our site for your personal use and
                        you may draw the attention of others within your
                        organisation to content posted on our site.
                    </Typography>
                    <Typography variant="body2">
                        You must not modify the paper or digital copies of any
                        materials you have printed off or downloaded in any way,
                        and you must not use any illustrations, photographs,
                        video or audio sequences or any graphics separately from
                        any accompanying text.
                    </Typography>
                    <Typography variant="body2">
                        Our status (and that of any identified contributors) as
                        the authors of content on our site must always be
                        acknowledged (except where the content is
                        user-generated).
                    </Typography>
                    <Typography variant="body2">
                        You must not use any part of the content on our site for
                        commercial purposes without obtaining a licence to do so
                        from us or our licensors.
                    </Typography>
                    <Typography variant="body2">
                        If you print off, copy, download, share or repost any
                        part of our site in breach of these terms of use, your
                        right to use our site will cease immediately and you
                        must, at our option, return or destroy any copies of the
                        materials you have made.
                    </Typography>

                    <Typography variant="h2">
                        No text or data mining, or web scraping
                    </Typography>
                    <Typography variant="body2">
                        You shall not conduct, facilitate, authorise or permit
                        any text or data mining or web scraping in relation to
                        our site or any services provided via, or in relation
                        to, our site for any purpose, including the development,
                        training, fine-tuning or validation of AI systems or
                        models. This includes using (or permitting, authorising
                        or attempting the use of):
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                    >
                        <Box component="li">
                            <Typography variant="body2">
                                Any &quot;robot&quot;, &quot;bot&quot;,
                                &quot;spider&quot;, &quot;scraper&quot; or other
                                automated device, program, tool, algorithm,
                                code, process or methodology to access, obtain,
                                copy, monitor or republish any portion of the
                                site or any data, content, information or
                                services accessed via the same.
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                Any automated analytical technique aimed at
                                analysing text and data in digital form to
                                generate information or develop, train,
                                fine-tune or validate AI systems or models which
                                includes but is not limited to patterns, trends
                                and correlations.
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2">
                        The provisions in this clause should be treated as an
                        express reservation of our rights in this regard,
                        including for the purposes of Article 4(3) of Digital
                        Copyright Directive ((EU) 2019/790).
                    </Typography>
                    <Typography variant="body2">
                        You shall not use, and we do not consent to the use of,
                        our site, or any data published by, or contained in, or
                        accessible via, our site or any services provided via,
                        or in relation to, our site for the purposes of
                        developing, training, fine-tuning or validating any AI
                        system or model or for any other purposes.
                    </Typography>
                    <Typography variant="body2">
                        This clause shall not apply insofar as (but only to the
                        extent that) we are unable to exclude or limit text or
                        data mining or web scraping activity by contract under
                        the laws which are applicable to us.
                    </Typography>

                    <Typography variant="h2">
                        Rules about linking to our site
                    </Typography>
                    <Typography variant="body2">
                        You may link to our home page, provided you request and
                        receive our consent in writing, and do so in a way that
                        is fair and legal and does not damage our reputation or
                        take advantage of it.
                    </Typography>
                    <Typography variant="body2">
                        You must not establish a link in such a way as to
                        suggest any form of association, approval or endorsement
                        on our part where none exists.
                    </Typography>
                    <Typography variant="body2">
                        You must not establish a link to our site in any website
                        that is not owned by you.
                    </Typography>
                    <Typography variant="body2">
                        Our site must not be framed on any other site, nor may
                        you create a link to any part of our site other than the
                        home page.
                    </Typography>
                    <Typography variant="body2">
                        We reserve the right to withdraw linking permission
                        without notice.
                    </Typography>
                    <Typography variant="body2">
                        If you wish to link to or make any use of content on our
                        site other than that set out above, please contact the
                        email address set out above.
                    </Typography>

                    <Typography variant="h2">
                        Do not rely on information on this site
                    </Typography>
                    <Typography variant="body2">
                        The content on our site is provided for general
                        information only. It is not intended to amount to advice
                        on which you should rely. You must obtain professional
                        or specialist advice before taking, or refraining from,
                        any action on the basis of the content on our site.
                    </Typography>
                    <Typography variant="body2">
                        Although we make reasonable efforts to update the
                        information on our site, we make no representations,
                        warranties or guarantees, whether express or implied,
                        that the content on our site is accurate, complete or up
                        to date and in some instances the data may be dummy data
                        for demonstration purposes.
                    </Typography>

                    <Typography variant="h2">
                        We are not responsible for websites we link to
                    </Typography>
                    <Typography variant="body2">
                        Where our site contains links to other sites and
                        resources provided by third parties, these links are
                        provided for your information only. Such links should
                        not be interpreted as approval by us of those linked
                        websites or information you may obtain from them.
                    </Typography>
                    <Typography variant="body2">
                        We have no control over the contents of those sites or
                        resources.
                    </Typography>

                    <Typography variant="h2">
                        User-generated content is not approved by us
                    </Typography>
                    <Typography variant="body2">
                        This website may include information and materials
                        uploaded by other users of our site, including to social
                        media pages, video-sharing sites, bulletin boards and
                        chat rooms. This information and these materials have
                        not been verified or approved by us. The views expressed
                        by other users on our site do not represent our views or
                        values.
                    </Typography>

                    <Typography variant="h2">
                        How to complain about or report content
                    </Typography>
                    <Typography variant="body2">
                        If you become aware of any material that could comprise
                        or be connected to child sexual abuse or exploitation or
                        that could comprise terrorist content or be connected to
                        terrorism, please contact us immediately using the
                        details above in Who we are and how to contact us.
                    </Typography>

                    <Typography variant="h2">
                        Our responsibility for loss or damage suffered by you
                    </Typography>
                    <Typography variant="body2">
                        Whether you are a consumer or a business user:
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                    >
                        <Box component="li">
                            <Typography variant="body2">
                                We do not exclude or limit in any way our
                                liability to you where it would be unlawful to
                                do so. This includes liability for death or
                                personal injury caused by our negligence or the
                                negligence of our employees, agents or
                                subcontractors and for fraud or fraudulent
                                misrepresentation.
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                Different limitations and exclusions of
                                liability will apply to liability arising as a
                                result of the supply of any products to you,
                                which will be set out in separate terms and
                                conditions.
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2">
                        If you are a business user:
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                    >
                        <Box component="li">
                            <Typography variant="body2">
                                We exclude all implied conditions, warranties,
                                representations or other terms that may apply to
                                our site or any content on it.
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                We will not be liable to you for any loss or
                                damage, whether in contract, tort (including
                                negligence), breach of statutory duty, or
                                otherwise, even if foreseeable, arising under or
                                in connection with:
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                use of, or inability to use, our site; or
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                use of or reliance on any content displayed on
                                our site.
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                In particular, we will not be liable for:
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                loss of profits, sales, business, or revenue;
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                business interruption;
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                loss of business opportunity, goodwill or
                                reputation;
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                any indirect or consequential loss or damage.
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2">
                        If you are a consumer user:
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ listStyleType: "disc", paddingLeft: "30px" }}
                    >
                        <Box component="li">
                            <Typography variant="body2">
                                We do not permit consumers to use our site. If
                                you access our site please remove yourself from
                                our site.
                            </Typography>
                        </Box>
                        <Box component="li">
                            <Typography variant="body2">
                                If you continue to use our site, you agree not
                                to use our site for any commercial or business
                                purposes, and we have no liability to you for
                                any loss of profit, loss of business, business
                                interruption, or loss of business opportunity.
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="h2">
                        How we may use your personal information
                    </Typography>
                    <Typography variant="body2">
                        We will only use your personal information as set out in
                        our{" "}
                        <Typography
                            variant="body2"
                            sx={{
                                color: "var(--proda-neon-blue)",
                            }}
                            onClick={() => {
                                window.open(
                                    "https://proda.ai/privacy-policy/",
                                    "_self",
                                );
                            }}
                        >
                            Privacy Policy
                        </Typography>
                        .
                    </Typography>

                    <Typography variant="h2">
                        We are not responsible for viruses and you must not
                        introduce them.
                    </Typography>
                    <Typography variant="body2">
                        We do not guarantee that our site will be secure or free
                        from bugs or viruses.
                    </Typography>
                    <Typography variant="body2">
                        You are responsible for configuring your information
                        technology, computer programmes and platform to access
                        our site. You should use your own virus protection
                        software.
                    </Typography>
                    <Typography variant="body2">
                        You must not misuse our site by knowingly introducing
                        viruses, trojans, worms, logic bombs or other material
                        that is malicious or technologically harmful. You must
                        not attempt to gain unauthorised access to our site, the
                        server on which our site is stored or any server,
                        computer or database connected to our site. You must not
                        attack our site via a denial-of-service attack or a
                        distributed denial-of service attack. By breaching this
                        provision, you would commit a criminal offence under the
                        Computer Misuse Act 1990. We will report any such breach
                        to the relevant law enforcement authorities and we will
                        co-operate with those authorities by disclosing your
                        identity to them. In the event of such a breach, your
                        right to use our site will cease immediately.
                    </Typography>

                    <Typography variant="h2">
                        Which country&apos;s laws apply to any disputes?
                    </Typography>
                    <Typography variant="body2">
                        If you are a consumer, please note that these terms of
                        use, their subject matter and their formation, are
                        governed by English law. You and we both agree that the
                        courts of England and Wales will have exclusive
                        jurisdiction except that if you are a resident of
                        Northern Ireland you may also bring proceedings in
                        Northern Ireland, and if you are resident of Scotland,
                        you may also bring proceedings in Scotland.
                    </Typography>
                    <Typography variant="body2">
                        If you are a business, these terms of use, their subject
                        matter and their formation (and any non-contractual
                        disputes or claims) are governed by English law. We both
                        agree to the exclusive jurisdiction of the courts of
                        England and Wales.
                    </Typography>

                    <Typography variant="h2">
                        Our trademarks are registered
                    </Typography>
                    <Typography variant="body2">
                        You are not permitted to use them without our approval,
                        unless they are part of material you are using as
                        permitted under{" "}
                        <b>How you may use material on our site.</b>
                    </Typography>
                </Card>
            </Box>
        );
    };

    return ViewUserPolicies;
};
